import { IconButton } from "@material-ui/core";
import { MoreVertRounded } from "@material-ui/icons";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import api from "../../../services/api";
import { toast } from "react-toastify";
import { COLORS } from "../../../assets/colors";

const tableHeaders = [
  "Passenger",
  "Contact",
  "Seats",
  "Driver",
  "Driver Contact",
  "Payment Status",
  "Booking Date",
  "Ride ID",
  "Amount",
  "Actions",
];

const BookingsTable = ({ bookings, fetchBookings }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeItem, setActiveItem] = useState(null);
  const ddref = useRef();

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      setIsOpen(false);
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  const handleOutsideClick = (event) => {
    if (ddref.current && !ddref.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  const toggleDropdown = (item) => {
    if (activeItem?.id === item.id) {
      setIsOpen(!isOpen);
    } else {
      setActiveItem(item);
      setIsOpen(true);
    }
  };

  return (
    <TableContainer>
      <StyledTable>
        <thead>
          <tr>
            {tableHeaders.map((header, index) => (
              <th key={index}>{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {bookings?.map((row, rowIndex) => (
            <TableRow key={rowIndex}>
              <TableCell>
                <div className="user-info">
                  <div className="avatar-wrapper">
                    <img
                      src={
                        row.requestedBy.avatar_url ||
                        `https://ui-avatars.com/api/?name=${row?.requestedBy.first_name}&background=random`
                      }
                      alt="avatar"
                    />
                  </div>
                  <span className="bold">{row.requestedBy.first_name}</span>
                </div>
              </TableCell>
              <TableCell>{row.requestedBy.mobile_number}</TableCell>
              <TableCell>
                <span className="seats-badge">
                  {row.front_seats > 0 && row.back_seats > 0
                    ? `${row.seats} Seats (${row.front_seats} Front, ${row.back_seats} Back)`
                    : row.front_seats > 0
                    ? `${row.seats} Front Seat${row.seats > 1 ? "s" : ""}`
                    : row.back_seats > 0
                    ? `${row.seats} Back Seat${row.seats > 1 ? "s" : ""}`
                    : `${row.seats} Seats`}
                </span>
              </TableCell>
              <TableCell className="bold">{row.requestedTo.first_name}</TableCell>
              <TableCell>{row.requestedTo.mobile_number}</TableCell>
              <TableCell>
                <StatusBadge paid={row.has_paid}>
                  {row.has_paid ? "Paid" : "Pending"}
                </StatusBadge>
              </TableCell>
              <TableCell>{moment(row.created_at).format("DD MMM, YYYY")}</TableCell>
              <TableCell>
                <code className="ride-id">#{row.Ride.id}</code>
              </TableCell>
              <TableCell className="bold amount">
                Rs.{parseInt(row.Ride.ticket * 0.1 * row.seats)}
              </TableCell>
              <TableCell>
                <ActionWrapper>
                  <IconButton onClick={() => toggleDropdown(row)}>
                    <MoreVertRounded />
                  </IconButton>
                  {activeItem?.id === row.id && isOpen && (
                    <DropdownMenu ref={ddref}>
                      <MenuItem onClick={() => toast.info("View Details coming soon")}>
                        View Details
                      </MenuItem>
                      <MenuItem onClick={() => toast.info("Refund handling coming soon")}>
                        Process Refund
                      </MenuItem>
                    </DropdownMenu>
                  )}
                </ActionWrapper>
              </TableCell>
            </TableRow>
          ))}
        </tbody>
      </StyledTable>
    </TableContainer>
  );
};

export default BookingsTable;

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  text-align: left;

  th {
    padding: 1rem;
    background: #F8FAFC;
    color: ${COLORS.adminTextSecondary};
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  }
`;

const TableRow = styled.tr`
  border-bottom: 1px solid rgba(0, 0, 0, 0.03);
  transition: background 0.2s ease;

  &:hover {
    background: rgba(56, 189, 248, 0.03);
  }
`;

const TableCell = styled.td`
  padding: 1rem;
  color: ${COLORS.adminTextPrimary};
  font-size: 0.875rem;
  vertical-align: middle;

  &.bold {
    font-weight: 600;
  }

  &.amount {
    color: ${COLORS.adminSuccess};
  }

  .user-info {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .avatar-wrapper {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    overflow: hidden;
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .seats-badge {
    padding: 0.2rem 0.5rem;
    background: #F1F5F9;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 500;
  }

  .ride-id {
    font-family: monospace;
    color: ${COLORS.adminTextSecondary};
  }
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  display: inline-flex;
  background: ${props => props.paid ? "rgba(16, 185, 129, 0.1)" : "rgba(245, 158, 11, 0.1)"};
  color: ${props => props.paid ? COLORS.adminSuccess : COLORS.adminWarning};
`;

const ActionWrapper = styled.div`
  position: relative;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background: #fff;
  border-radius: 8px;
  box-shadow: ${COLORS.adminShadow};
  border: 1px solid rgba(0, 0, 0, 0.05);
  z-index: 100;
  min-width: 160px;
  padding: 0.5rem;
`;

const MenuItem = styled.button`
  display: block;
  width: 100%;
  padding: 0.6rem 1rem;
  border: none;
  background: none;
  text-align: left;
  font-size: 0.875rem;
  cursor: pointer;
  border-radius: 4px;
  &:hover {
    background: #F1F5F9;
  }
`;
